import React, { useState, useEffect } from 'react';
import { OrderTrackingSkeleton } from '../Common/PageSkeleton';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { getSessionToken } from '../../utils/authSession';
import { useTranslate } from "../../Hook/useTranslate";
import { useLang } from '../../context/LanguageContext';
import { io } from 'socket.io-client';
import useReorder from '../../Hook/useReorder';
import { formatMoney } from '../../utils/formatMoney';
import FormBanner from '../Common/FormBanner';

/* Inline styles */
const S = {
  page: {
    minHeight: '100vh',
    background: '#fff',
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },

  /* ── Header ── */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 28px',
    height: 62,
    background: '#fff',
    borderBottom: '1px solid #eee',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  logoBox: {
    width: 40, height: 40,
    background: '#e62e2e',
    borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  headerTitle: { fontSize: 16, fontWeight: 700, color: '#111', letterSpacing: '-0.3px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 20 },
  headerBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 14, color: '#555', fontWeight: 500,
  },

  /*Content wrapper*/
  content: { maxWidth: 640, margin: '0 auto', padding: '28px 16px 40px' },

  /*Progress Stepper*/
  stepper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
    position: 'relative',
    padding: '8px 0 0',
  },
  stepWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1 },
  stepEmoji: { fontSize: 28, lineHeight: 1, marginBottom: 10 },
  stepDot: (active) => ({
    width: 16, height: 16,
    borderRadius: '50%',
    background: active ? '#f5a623' : 'transparent',
    border: `2px solid ${active ? '#f5a623' : '#f5a623'}`,
    flexShrink: 0,
  }),
  stepLine: (filled) => ({
    flex: 1,
    height: 2,
    background: '#f5a623',
    opacity: filled ? 1 : 0.35,
    marginTop: 38, // align with dots row
    alignSelf: 'flex-end',
    marginBottom: 0,
    position: 'relative',
    top: -8,
  }),

  /*Order Cards*/
  cardWrap: { display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 },
  card: {
    border: '1px solid #e8e8e8',
    borderRadius: 14,
    overflow: 'hidden',
    background: '#fff',
    cursor: 'pointer',
    boxShadow: '0 1px 5px rgba(0,0,0,0.06)',
  },
  cardInner: { padding: '14px 16px 0' },

  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    paddingBottom: 12,
    borderBottom: '1px solid #f0f0f0',
  },
  restaurantName: { fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 3 },
  restaurantCategory: { fontSize: 12.5, color: '#888' },

  arrowBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '2px 0 0 8px', flexShrink: 0,
  },

  itemsRow: {
    display: 'flex', alignItems: 'center', gap: 20,
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0',
    flexWrap: 'wrap',
  },
  itemGroup: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  itemImg: {
    width: 52, height: 52, borderRadius: 10,
    background: '#f0e8d8',
    overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  itemName: { fontSize: 13, fontWeight: 600, color: '#111' },
  itemSub: { fontSize: 11.5, color: '#888' },
  moreItems: { fontSize: 13, color: '#555', marginLeft: 'auto', alignSelf: 'flex-end' },

  cardFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '12px 16px 10px',
  },
  dateTimeWrap: {},
  dateText: { fontSize: 13, color: '#444' },
  timeText: { fontSize: 13, color: '#444' },
  totalText: { fontSize: 15, fontWeight: 700, color: '#111' },

  status: (color) => ({
    fontSize: 13, fontWeight: 600,
    color: color === 'orange' ? '#f5a623' : '#e62e2e',
    paddingBottom: 14,
    paddingLeft: 16,
  }),

  /* Reorder button */
  reorderBtn: {
    display: 'block', width: '100%',
    padding: '15px 0',
    background: '#e62e2e',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontSize: 16, fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.4px',
    marginTop: 8,
  },
  actionBtnRow: {
    display: 'flex', gap: 10, padding: '0 16px 16px'
  },
  smallBtn: (bg) => ({
    flex: 1, padding: '8px 0', borderRadius: 8, border: 'none',
    background: bg, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer'
  }),

  /* Rating Modal */
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  },
  modalContent: {
    background: '#fff', padding: 24, borderRadius: 16, width: '90%', maxWidth: 400, textAlign: 'center'
  },
  starRow: { display: 'flex', justifyContent: 'center', gap: 10, margin: '20px 0' },
  star: (active) => ({
    fontSize: 32, cursor: 'pointer', color: active ? '#f5a623' : '#ddd', border: 'none', background: 'none'
  }),
  textarea: {
    width: '100%', padding: 12, borderRadius: 8, border: '1px solid #ddd',
    minHeight: 80, marginBottom: 20, fontFamily: 'inherit', fontSize: 14
  }
};

/* Tiny SVG helpers */
function LogoSVG() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <ellipse cx="12" cy="7" rx="8" ry="3.5" fill="white" opacity="0.95" />
      <rect x="4" y="7" width="16" height="2.5" fill="white" opacity="0.7" />
      <circle cx="8.5" cy="17" r="2.8" fill="white" />
      <circle cx="15.5" cy="17" r="2.8" fill="white" />
      <rect x="10.5" y="9.5" width="3" height="5.5" fill="white" opacity="0.8" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M7 4L13 10L7 16" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="9" stroke="#555" strokeWidth="1.5" />
      <path d="M7.5 7.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 3.5" stroke="#555" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="10" cy="14.5" r="0.9" fill="#555" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="7" r="3.5" stroke="#555" strokeWidth="1.5" />
      <path d="M3 18c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="#555" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/* Placeholder food image SVG */
function FoodImg({ bg = '#f0e8d8' }) {
  return (
    <div style={{ ...S.itemImg, background: bg }}>
      <svg width="36" height="36" viewBox="0 0 36 36">
        <ellipse cx="18" cy="22" rx="11" ry="7" fill="#f5a623" />
        <ellipse cx="18" cy="20" rx="9" ry="5.5" fill="#e8882a" />
        <circle cx="13" cy="18" r="1.8" fill="#c0392b" />
        <circle cx="19" cy="16" r="1.8" fill="#c0392b" />
        <circle cx="24" cy="18" r="1.8" fill="#c0392b" />
        <rect x="16" y="10" width="4" height="8" rx="1" fill="#8B4513" />
      </svg>
    </div>
  );
}

/* Stepper component */
const STEPS = [
  { emoji: '🧾', label: 'Order' },
  { emoji: '👨‍🍳', label: 'Preparing' },
  { emoji: '🛵', label: 'On the way' },
  { emoji: '📦', label: 'Delivered' },
];

function ProgressStepper({ activeStep = 1 }) {
  return (
    <div style={{ padding: '0 8px', marginBottom: 28 }}>
      {/* Emoji row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        {STEPS.map((s, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: i < STEPS.length - 1 ? 1 : 'none' }}>
            <span style={{ fontSize: 28 }}>{s.emoji}</span>
          </div>
        ))}
      </div>

      {/* Dot + line row */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <div style={{
              width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
              background: i <= activeStep ? '#f5a623' : 'transparent',
              border: '2.5px solid #f5a623',
              transition: 'background 0.3s',
            }} />
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2,
                background: '#f5a623',
                opacity: i < activeStep ? 1 : 0.3,
              }} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* Order Card */
function OrderCard({ order, onClick, getLangText, t, onRate, onRefund, onReorder }) {
  const statusColors = { preparing: '#f5a623', ongoing: '#e62e2e', delivered: '#2cb87e' };
  const statusKey = (order.status || '').toLowerCase();
  const statusColor = statusColors[statusKey] || '#888';

  return (
    <div style={S.card} onClick={onClick}>
      <div style={S.cardInner}>

        {/* Restaurant header */}
        <div style={S.cardHeader}>
          <div>
            <div style={S.restaurantName}>{getLangText(order.restaurant)}</div>
            <div style={S.restaurantCategory}>{order.category}</div>
          </div>
          <button style={S.arrowBtn}><ChevronRight /></button>
        </div>

        {/* Items row */}
        <div style={S.itemsRow}>
          {Array.isArray(order.items) && order.items.slice(0, 2).map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={S.itemName}>{getLangText(item)}</span>
                <span style={S.itemSub}>Original</span>
              </div>
              <FoodImg bg={i === 0 ? '#f5e6cc' : '#f0e8d8'} />
            </div>
          ))}
          {order.moreItems > 0 && (
            <span style={S.moreItems}>+{order.moreItems} {t ? t('more') : 'More'}</span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={S.cardFooter}>
        <div>
          <div style={S.dateText}>
            {t ? t('orderPlacedOn') : 'Order placed on'} {order.date}
          </div>
          <div style={S.timeText}>{order.time}</div>
        </div>
        <div style={S.totalText}>{order.total}</div>
      </div>

      {/* Status badge */}
      <div style={{ ...S.status(order.statusColor), color: statusColor }}>
        {t ? t(statusKey) : order.status}
      </div>

      {statusKey === 'delivered' && (
        <div style={S.actionBtnRow} onClick={(e) => e.stopPropagation()}>
          <button
            style={S.smallBtn('#48c479')}
            onClick={() => onRate(order.id)}
          >
            {t ? t("Rate Order") : "Rate Order"}
          </button>
          <button style={S.smallBtn('#e62e2e')} onClick={() => onReorder(order.id)}>
            {t ? t("Reorder") : "Reorder"}
          </button>

          {['processed', 'approved', 'completed'].includes(order.refundStatus) ? (
            <div style={{
              padding: '6px 14px',
              borderRadius: '8px',
              backgroundColor: '#e6f4ea',
              color: '#16a34a',
              border: '1px solid #bbf7d0',
              fontSize: '12px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              ✓ Refund Approved
            </div>
          ) : ['pending', 'pending_admin', 'sent_to_resto', 'resto_verified', 'in_progress'].includes(order.refundStatus) ? (
            <div style={{
              padding: '6px 14px',
              borderRadius: '8px',
              backgroundColor: '#fef3c7',
              color: '#d97706',
              border: '1px solid #fde68a',
              fontSize: '12px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              ⏳ Refund Requested
            </div>
          ) : ['rejected'].includes(order.refundStatus) ? (
            <div style={{
              padding: '6px 14px',
              borderRadius: '8px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: '1px solid #fecaca',
              fontSize: '12px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              ✕ Refund Rejected
            </div>
          ) : (
            <button
              style={S.smallBtn('#666')}
              onClick={() => onRefund(order)}
            >
              Refund Request
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/*Main Component */
const OrderTracking = () => {
  const navigate = useNavigate();
  const t = useTranslate();
  const { lang } = useLang();
  const [orders, setOrders] = useState([]);
  const [refundsMap, setRefundsMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formBanner, setFormBanner] = useState({ type: '', text: '' });
  const [ratingError, setRatingError] = useState('');
  const [refundError, setRefundError] = useState('');

  /* Star Rating Modal State */
  const [ratingModal, setRatingModal] = useState({ open: false, orderId: null, rating: 5, comment: '' });

  /* Item-Level Refund Modal State */
  const [refundModal, setRefundModal] = useState({
    open: false,
    orderId: null,
    orderItems: [],
    selectedItems: {},
    note: '',
    evidenceFiles: [],
    currencySymbol: ''
  });

  useEffect(() => {
    const fetchOrdersAndRefunds = async () => {
      try {
        const [ordersRes, refundsRes] = await Promise.allSettled([
          api.get('/orders/my-orders'),
          api.get('/web-user/refunds')
        ]);

        if (ordersRes.status === 'fulfilled') {
          setOrders(ordersRes.value.data.orders || []);
        }

        if (refundsRes.status === 'fulfilled') {
          const refundList = refundsRes.value.data.refunds || [];
          const refMap = {};
          refundList.forEach(r => {
            const oid = r.order?._id || r.order;
            if (oid) {
              refMap[oid.toString()] = r;
            }
          });
          setRefundsMap(refMap);
        }
      } catch (err) {
        console.error("Error fetching orders & refunds:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrdersAndRefunds();

    // Socket.io Integration for Real-Time Tracking
    const token = getSessionToken();
    if (!token) return;

    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const socketUrl = apiBase.replace(/\/api\/?$/, ''); // Remove trailing /api

    const socket = io(socketUrl, {
      auth: { token }
    });

    socket.on('connected', (data) => {
    });

    socket.on('order:status', (data) => {
      setOrders(prevOrders => prevOrders.map(order =>
        String(order._id) === String(data.orderId) ? { ...order, status: data.status } : order
      ));
      // The delivery code appears once a rider is on the way (and disappears after delivery).
      api.get(`/orders/${data.orderId}/customer`)
        .then((res) => {
          const fresh = res.data?.order || res.data?.data || res.data;
          setOrders(prevOrders => prevOrders.map(order =>
            String(order._id) === String(data.orderId) ? { ...order, deliveryCode: fresh?.deliveryCode } : order
          ));
        })
        .catch(() => {});
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleRateOrder = (orderId) => {
    setRatingError('');
    setRatingModal({ ...ratingModal, open: true, orderId, rating: 5, comment: '' });
  };

  const submitRating = async () => {
    if (isSubmitting) return;
    const { orderId, rating, comment } = ratingModal;

    try {
      setIsSubmitting(true);
      setRatingError('');
      await api.post('/reviews', {
        orderId: orderId,
        restaurantRating: rating,
        riderRating: rating,
        comment
      });
      setFormBanner({ type: 'success', text: t("Thank you for your rating!") });
      setRatingModal({ ...ratingModal, open: false });
    } catch (err) {
      setRatingError(err.response?.data?.message || t("Something went wrong."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefundRequest = (order) => {
    const items = order.rawItems || [];
    const initialSelected = {};
    items.forEach(item => {
      initialSelected[item._id] = {
        selected: false,
        quantity: item.quantity || 1,
        reason: 'missing_item'
      };
    });

    setRefundError('');
    setRefundModal({
      open: true,
      orderId: order.id,
      orderItems: items,
      selectedItems: initialSelected,
      note: '',
      evidenceFiles: [],
      currencySymbol: order.currencySymbol || ''
    });
  };

  const handleItemSelectToggle = (itemId) => {
    setRefundModal(prev => ({
      ...prev,
      selectedItems: {
        ...prev.selectedItems,
        [itemId]: {
          ...prev.selectedItems[itemId],
          selected: !prev.selectedItems[itemId]?.selected
        }
      }
    }));
  };

  const handleItemChange = (itemId, field, value) => {
    setRefundModal(prev => ({
      ...prev,
      selectedItems: {
        ...prev.selectedItems,
        [itemId]: {
          ...prev.selectedItems[itemId],
          [field]: value
        }
      }
    }));
  };

  const submitRefundRequest = async () => {
    if (isSubmitting) return;
    const { orderId, orderItems, selectedItems, note, evidenceFiles } = refundModal;

    const itemsPayload = [];
    orderItems.forEach(item => {
      const sel = selectedItems[item._id];
      if (sel && sel.selected) {
        itemsPayload.push({
          productId: item.product ? (item.product._id || item.product) : item._id,
          quantity: Number(sel.quantity) || 1,
          reason: sel.reason || 'missing_item'
        });
      }
    });

    if (itemsPayload.length === 0 && !note) {
      setRefundError(t("Please select at least one item or provide a note for the refund."));
      return;
    }

    try {
      setIsSubmitting(true);
      setRefundError('');

      const formData = new FormData();
      formData.append('orderId', orderId);
      formData.append('note', note || "Requested refund for order");
      formData.append('items', JSON.stringify(itemsPayload));
      
      if (Array.isArray(evidenceFiles)) {
        evidenceFiles.forEach(file => {
          formData.append('webEvidenceImages', file);
        });
      }

      const res = await api.post('/web-user/refund-request', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setRefundsMap(prev => ({
        ...prev,
        [orderId.toString()]: { status: 'pending_admin' }
      }));
      setFormBanner({
        type: 'success',
        text: res.data?.message || "Refund request submitted successfully!"
      });
      setRefundModal({ open: false, orderId: null, orderItems: [], selectedItems: {}, note: '', evidenceFiles: [], currencySymbol: '' });
    } catch (err) {
      setRefundError(err.response?.data?.message || t("Refund request failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Map backend status to stepper index (0-3)
  const getActiveStep = (status) => {
    const s = status?.toLowerCase();
    if (['placed'].includes(s)) return 0;
    if (['accepted', 'preparing'].includes(s)) return 1;
    if (['ready', 'picked_up', 'out_for_delivery'].includes(s)) return 2;
    if (['delivered'].includes(s)) return 3;
    return 0;
  };

  const { reorder, isReordering } = useReorder(setFormBanner);
  const handleReorder = (orderId = orders?.[0]?._id) => reorder(orderId);




  const getLangText = (data) => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
      return data.en || data.En || data.EN || Object.values(data).find(v => typeof v === 'string') || '';
    }
    return String(data);
  };

  // Format backend order for the UI component
  const formattedOrders = orders.map(o => {
    const refundObj = refundsMap[o._id.toString()] || o.refund || {};
    const refStatus = (refundObj.status || refundObj.verificationStatus || '').toLowerCase();

    return {
      id: o._id,
      restaurant: o.restaurant?.name || 'Restaurant',
      category: Array.isArray(o.restaurant?.cuisine) ? o.restaurant.cuisine.join(', ') : 'Food',
      items: o.items?.map(i => i.name) || [],
      rawItems: o.items || [],
      moreItems: Math.max(0, (o.items?.length || 0) - 2),
      date: new Date(o.createdAt).toLocaleDateString(lang === 'ar' ? 'ar-EG' : (lang === 'de' ? 'de-DE' : 'en-GB'), { day: '2-digit', month: 'short' }),
      time: new Date(o.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      status: o.status.charAt(0).toUpperCase() + o.status.slice(1),
      statusColor: o.status === 'delivered' ? 'green' : (o.status === 'cancelled' ? 'red' : 'orange'),
      total: formatMoney(o.totalAmount, { symbol: o.currencySymbol }),
      rawStatus: o.status,
      deliveryCode: o.deliveryCode,
      refundStatus: refStatus,
      refundInfo: refundObj
    };
  });

  // Use the most recent active order for the stepper, or the last order
  const latestOrder = formattedOrders[0];
  const activeStep = latestOrder ? getActiveStep(latestOrder.rawStatus) : 0;

  if (loading) return <OrderTrackingSkeleton />;


  return (
    <div style={S.page}>

      {/* ── Header ── */}
      <header style={S.header}>
        <div style={S.headerLeft}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', marginRight: '5px' }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M14 5l-6 6 6 6" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div style={S.logoBox}>
            <LogoSVG />
          </div>
          <span style={S.headerTitle}>{t("Order Tracking")}</span>
        </div>

        <div style={S.headerRight}>
          <button style={S.headerBtn}>
            <HelpIcon /> {t("Help")}
          </button>
          <button style={S.headerBtn} onClick={() => navigate('/profile')}>
            <UserIcon /> {t("account")}
          </button>
        </div>
      </header>

      {/* ── Content ── */}
      <div style={S.content}>
        <FormBanner
          message={formBanner.text}
          type={formBanner.type || 'error'}
          onDismiss={() => setFormBanner({ type: '', text: '' })}
        />

        {/* Progress stepper based on latest order */}
        {formattedOrders.length > 0 && <ProgressStepper activeStep={activeStep} />}
        {latestOrder?.deliveryCode && (
          <div role="status" style={{ margin: '12px 16px', padding: '12px 16px', borderRadius: '12px', background: '#FEF2F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ fontSize: '13px', color: '#7F1D1D', fontWeight: 600 }}>{t("Share this code with your delivery partner to receive your order") || "Share this code with your delivery partner to receive your order"}</span>
            <span style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '4px', color: '#B91C1C' }}>{latestOrder.deliveryCode}</span>
          </div>
        )}

        {/* Order cards */}
        <div style={S.cardWrap}>
          {formattedOrders.length > 0 ? (
            formattedOrders.map((order, idx) => (
              <OrderCard
                key={idx}
                order={order}
                onClick={() => navigate(`/order-details/${order.id}`)}
                getLangText={getLangText}
                t={t}
                onRate={handleRateOrder}
                onRefund={handleRefundRequest}
                onReorder={handleReorder}
              />
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>{t("No orders found.")}</div>
          )}
        </div>

        {/* Star Rating Modal */}
        {ratingModal.open && (
          <div style={S.modalOverlay}>
            <div style={S.modalContent}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{t("Rate your Experience")}</h3>
              <FormBanner
                message={ratingError}
                type="error"
                onDismiss={() => setRatingError('')}
              />
              <div style={S.starRow}>
                {[1, 2, 3, 4, 5].map((num) => (
                  <button
                    key={num}
                    style={S.star(num <= ratingModal.rating)}
                    onClick={() => setRatingModal({ ...ratingModal, rating: num })}
                  >
                    ★
                  </button>
                ))}
              </div>
              <textarea
                style={S.textarea}
                placeholder={t("Tell us more about the food and delivery...")}
                value={ratingModal.comment}
                onChange={(e) => setRatingModal({ ...ratingModal, comment: e.target.value })}
              />
              <div style={{ display: 'flex', gap: 12 }}>
                <button style={S.smallBtn('#666')} onClick={() => { setRatingModal({ ...ratingModal, open: false }); setRatingError(''); }}>
                  {t("Cancel")}
                </button>
                <button
                  style={S.smallBtn('#e62e2e')}
                  onClick={submitRating}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Rating"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Item-Level Refund Modal */}
        {refundModal.open && (
          <div style={S.modalOverlay}>
            <div style={{ ...S.modalContent, maxWidth: 480, textAlign: 'left' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#111' }}>{t("Request Item-Level Refund")}</h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666' }}>{t("Select the items that had issues and provide details.")}</p>
              <FormBanner
                message={refundError}
                type="error"
                onDismiss={() => setRefundError('')}
              />

              <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 16, paddingRight: 8 }}>
                {refundModal.orderItems.map((item, idx) => {
                  const sel = refundModal.selectedItems[item._id] || {};
                  const itemName = item.name || (item.product?.name) || `Item #${idx + 1}`;
                  const itemPrice = item.price || (item.product?.price) || 0;
                  return (
                    <div key={item._id || idx} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#111' }}>
                          <input
                            type="checkbox"
                            checked={!!sel.selected}
                            onChange={() => handleItemSelectToggle(item._id)}
                            style={{ width: 16, height: 16, cursor: 'pointer' }}
                          />
                          {itemName} ({item.quantity || 1} x {formatMoney(itemPrice, { symbol: refundModal.currencySymbol })})
                        </label>
                      </div>

                      {sel.selected && (
                        <div style={{ display: 'flex', gap: 10, marginTop: 8, paddingLeft: 26 }}>
                          <select
                            value={sel.reason}
                            onChange={(e) => handleItemChange(item._id, 'reason', e.target.value)}
                            style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }}
                          >
                            <option value="missing_item">{t("Missing Item")}</option>
                            <option value="quality_issue">{t("Quality Issue / Burnt")}</option>
                            <option value="spill_damage">{t("Spill / Damaged")}</option>
                            <option value="wrong_order">{t("Wrong Item")}</option>
                            <option value="late_delivery">{t("Late Delivery")}</option>
                            <option value="other">{t("Other")}</option>
                          </select>
                          <input
                            type="number"
                            min="1"
                            max={item.quantity || 1}
                            value={sel.quantity}
                            onChange={(e) => handleItemChange(item._id, 'quantity', e.target.value)}
                            style={{ width: 60, padding: '6px', borderRadius: 6, border: '1px solid #ccc', textAlign: 'center', fontSize: 13 }}
                            title="Quantity to refund"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {refundModal.orderItems.length === 0 && (
                  <div style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: '20px 0' }}>{t("No specific items found. Full order refund will be requested.")}</div>
                )}
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 4 }}>{t("Additional Note / Reason")}</label>
                <textarea
                  style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ccc', minHeight: 60, fontSize: 13, fontFamily: 'inherit' }}
                  placeholder={t("Describe the issue in detail...")}
                  value={refundModal.note}
                  onChange={(e) => setRefundModal({ ...refundModal, note: e.target.value })}
                />
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 4 }}>
                  {t("Evidence Images (Optional, Max 6)")}
                </label>
                <div style={{
                  border: '2px dashed #ddd',
                  borderRadius: 10,
                  padding: '16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: '#fcfcfc',
                  transition: 'border-color 0.2s',
                  position: 'relative'
                }}
                onClick={() => document.getElementById('evidence-upload').click()}
                onMouseOver={(e) => e.currentTarget.style.borderColor = '#e62e2e'}
                onMouseOut={(e) => e.currentTarget.style.borderColor = '#ddd'}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8, display: 'inline-block' }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>
                    {t("Click to select photos")}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    {t("Supports PNG, JPG, JPEG (Max 6 files)")}
                  </div>
                  <input
                    type="file"
                    id="evidence-upload"
                    multiple
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setRefundModal(prev => {
                        const currentFiles = prev.evidenceFiles || [];
                        const newFiles = [...currentFiles, ...files].slice(0, 6);
                        if (currentFiles.length + files.length > 6) {
                          setRefundError(t("Maximum 6 images are allowed."));
                        }
                        return { ...prev, evidenceFiles: newFiles };
                      });
                    }}
                  />
                </div>

                {/* File Previews */}
                {refundModal.evidenceFiles && refundModal.evidenceFiles.length > 0 && (
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 10,
                    marginTop: 12
                  }}>
                    {refundModal.evidenceFiles.map((file, fileIdx) => {
                      const previewUrl = URL.createObjectURL(file);
                      return (
                        <div key={fileIdx} style={{
                          position: 'relative',
                          width: 64,
                          height: 64,
                          borderRadius: 8,
                          overflow: 'hidden',
                          border: '1px solid #eee'
                        }}>
                          <img
                            src={previewUrl}
                            alt="preview"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <button
                            type="button"
                            style={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              background: 'rgba(0,0,0,0.6)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '50%',
                              width: 18,
                              height: 18,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: 10,
                              lineHeight: 1,
                              padding: 0
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setRefundModal(prev => {
                                const newFiles = prev.evidenceFiles.filter((_, idx) => idx !== fileIdx);
                                return { ...prev, evidenceFiles: newFiles };
                              });
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#666', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  onClick={() => setRefundModal({ open: false, orderId: null, orderItems: [], selectedItems: {}, note: '', evidenceFiles: [], currencySymbol: '' })}
                >
                  {t("Cancel")}
                </button>
                <button
                  style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#e62e2e', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  onClick={submitRefundRequest}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("Submitting...") : t("Submit Refund")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reorder */}
        <button
          style={{
            ...S.reorderBtn,
            opacity: isReordering ? 0.7 : 1,
            cursor: isReordering ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          disabled={isReordering}
          onClick={() => handleReorder()}
        >
          {isReordering ? (
            <>
              <svg className="location-spinning" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="2" x2="12" y2="6"></line>
                <line x1="12" y1="18" x2="12" y2="22"></line>
                <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                <line x1="2" y1="12" x2="6" y2="12"></line>
                <line x1="18" y1="12" x2="22" y2="12"></line>
                <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                <line x1="16.24" y1="4.93" x2="19.07" y2="7.76"></line>
              </svg>
              {t ? t('reordering', 'Reordering...') : 'Reordering...'}
            </>
          ) : (
            t ? t('reorder') : 'Reorder'
          )}
        </button>
      </div>
    </div>
  );
};

export default OrderTracking;